/**
 * Care Hooks
 *
 * React Query hooks for Care/Wellbeing operations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../providers/AuthProvider';
import { useSdkContext } from '../providers/SdkProvider';
import { requireJourney } from './require-journey';
import { TonnieApiError } from '../client/base';
import { heroesKeys } from './useHeroes';
import {
  createCareService,
  type CareAssessmentResult,
  type MoodCheckinInput,
  type QuickCheckinInput,
  type SubmitCheckpointInput,
  type CreateSupportRequestInput,
  type UpdateSupportRequestInput,
  type SubmitObjectiveFeedbackInput,
} from '../services/care.service';

/**
 * Don't retry on 404s — they mean "no data yet" for Care endpoints
 * (the user hasn't started the Care hero).
 */
function retryCareQuery(failureCount: number, error: Error): boolean {
  if (error instanceof TonnieApiError && error.isNotFound) return false;
  return failureCount < 3;
}

/**
 * Every Care read and write is ONE journey's (JOURNEYS-MULTI-ROUTE-PLAN · X6 ·
 * X7), so every hook takes the journey and stays idle until it is known — and
 * every key carries it: the pillars and the wellbeing summary were cached per
 * talent, so a journey switch served the other journey's numbers.
 */
type J = string | undefined;

export const careKeys = {
  all: ['care'] as const,
  assessment: (journeyId: J) => [...careKeys.all, 'assessment', journeyId ?? null] as const,
  pillars: (talentId: string, journeyId: J) =>
    [...careKeys.all, 'pillars', talentId, journeyId ?? null] as const,
  checkpoints: (talentId: string, journeyId: J) =>
    [...careKeys.all, 'checkpoints', talentId, journeyId ?? null] as const,
  checkpoint: (talentId: string, checkpointId: string, journeyId: J) =>
    [...careKeys.all, 'checkpoint', talentId, checkpointId, journeyId ?? null] as const,
  /** Every check-in list of a journey, whatever its filter — what a write invalidates. */
  checkinsOf: (talentId: string, journeyId: J) =>
    [...careKeys.all, 'checkins', talentId, journeyId ?? null] as const,
  checkins: (
    talentId: string,
    journeyId: J,
    params?: { type?: string; from?: string; to?: string }
  ) => [...careKeys.checkinsOf(talentId, journeyId), params] as const,
  strategy: (talentId: string, journeyId: J) =>
    [...careKeys.all, 'strategy', talentId, journeyId ?? null] as const,
  wellbeingSummary: (talentId: string, journeyId: J) =>
    [...careKeys.all, 'wellbeing-summary', talentId, journeyId ?? null] as const,
  momentum: (talentId: string, journeyId: J) =>
    [...careKeys.all, 'momentum', talentId, journeyId ?? null] as const,
  interactions: (talentId: string, journeyId: J) =>
    [...careKeys.all, 'interactions', talentId, journeyId ?? null] as const,
  support: (talentId: string, journeyId: J) =>
    [...careKeys.all, 'support', talentId, journeyId ?? null] as const,
  objectives: (talentId: string, journeyId: J) =>
    [...careKeys.all, 'objectives', talentId, journeyId ?? null] as const,
};

/**
 * A Care read: `null` for a 404 (no Care data yet — the hero not started, or a
 * plan without Care), anything else thrown.
 */
async function orNullOn404<T>(read: () => Promise<T>): Promise<T | null> {
  try {
    return await read();
  } catch (error) {
    if (error instanceof TonnieApiError && error.isNotFound) return null;
    throw error;
  }
}

/**
 * The journey's active Care assessment result.
 * Returns null if no assessment has been completed.
 */
export function useCareAssessment(journeyId: J) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const careService = createCareService(client);

  return useQuery<CareAssessmentResult | null>({
    queryKey: careKeys.assessment(journeyId),
    queryFn: () => orNullOn404(() => careService.getAssessment(requireJourney(journeyId))),
    enabled: isSignedIn && !!token && !!journeyId,
    staleTime: 0,
    retry: retryCareQuery,
  });
}

export function useCarePillars(talentId: string, journeyId: J) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const careService = createCareService(client);

  return useQuery({
    queryKey: careKeys.pillars(talentId, journeyId),
    queryFn: () => orNullOn404(() => careService.getPillars(talentId, requireJourney(journeyId))),
    enabled: isSignedIn && !!token && !!talentId && !!journeyId,
    staleTime: 1000 * 60 * 5,
    retry: retryCareQuery,
  });
}

export function useCareCheckpoints(talentId: string, journeyId: J) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const careService = createCareService(client);

  return useQuery({
    queryKey: careKeys.checkpoints(talentId, journeyId),
    queryFn: () =>
      orNullOn404(() => careService.getCheckpoints(talentId, requireJourney(journeyId))),
    enabled: isSignedIn && !!token && !!talentId && !!journeyId,
    retry: retryCareQuery,
  });
}

export function useCareCheckpoint(talentId: string, checkpointId: string, journeyId: J) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const careService = createCareService(client);

  return useQuery({
    queryKey: careKeys.checkpoint(talentId, checkpointId, journeyId),
    queryFn: () => careService.getCheckpoint(talentId, checkpointId, requireJourney(journeyId)),
    enabled: isSignedIn && !!token && !!talentId && !!checkpointId && !!journeyId,
  });
}

export function useSubmitCareCheckpoint(talentId: string, journeyId: J) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const careService = createCareService(client);

  return useMutation({
    mutationFn: ({ checkpointId, input }: { checkpointId: string; input: SubmitCheckpointInput }) =>
      careService.submitCheckpoint(talentId, checkpointId, input, requireJourney(journeyId)),
    onSuccess: (_, { checkpointId }) => {
      queryClient.invalidateQueries({ queryKey: careKeys.checkpoints(talentId, journeyId) });
      queryClient.invalidateQueries({
        queryKey: careKeys.checkpoint(talentId, checkpointId, journeyId),
      });
      queryClient.invalidateQueries({ queryKey: careKeys.pillars(talentId, journeyId) });
      queryClient.invalidateQueries({ queryKey: careKeys.wellbeingSummary(talentId, journeyId) });
      queryClient.invalidateQueries({ queryKey: heroesKeys.miles() });
    },
  });
}

export function useSubmitMoodCheckin(talentId: string, journeyId: J) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const careService = createCareService(client);

  return useMutation({
    mutationFn: (input: MoodCheckinInput) =>
      careService.submitMoodCheckin(talentId, input, requireJourney(journeyId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: careKeys.checkinsOf(talentId, journeyId) });
      queryClient.invalidateQueries({ queryKey: careKeys.wellbeingSummary(talentId, journeyId) });
      queryClient.invalidateQueries({ queryKey: heroesKeys.miles() });
    },
  });
}

export function useSubmitQuickCheckin(talentId: string, journeyId: J) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const careService = createCareService(client);

  return useMutation({
    mutationFn: (input: QuickCheckinInput) =>
      careService.submitQuickCheckin(talentId, input, requireJourney(journeyId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: careKeys.checkinsOf(talentId, journeyId) });
      queryClient.invalidateQueries({ queryKey: careKeys.wellbeingSummary(talentId, journeyId) });
      queryClient.invalidateQueries({ queryKey: careKeys.pillars(talentId, journeyId) });
      queryClient.invalidateQueries({ queryKey: heroesKeys.miles() });
    },
  });
}

export function useCareCheckins(
  talentId: string,
  journeyId: J,
  params?: { type?: string; from?: string; to?: string }
) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const careService = createCareService(client);

  return useQuery({
    queryKey: careKeys.checkins(talentId, journeyId, params),
    queryFn: () =>
      orNullOn404(() => careService.getCheckins(talentId, requireJourney(journeyId), params)),
    enabled: isSignedIn && !!token && !!talentId && !!journeyId,
    retry: retryCareQuery,
  });
}

/**
 * The journey's resolved Care strategy (Country←Company←Program←Cohort) — lets
 * the citizen Care Assessment present the COMPANY's configured pillars. Per
 * journey: two journeys may be governed by two companies (X7).
 */
export function useCareStrategy(talentId: string, journeyId: J) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const careService = createCareService(client);

  return useQuery({
    queryKey: careKeys.strategy(talentId, journeyId),
    queryFn: () => careService.getCareStrategy(talentId, requireJourney(journeyId)),
    enabled: isSignedIn && !!token && !!talentId && !!journeyId,
    staleTime: 1000 * 60 * 10,
  });
}

export function useCareWellbeingSummary(talentId: string, journeyId: J) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const careService = createCareService(client);

  return useQuery({
    queryKey: careKeys.wellbeingSummary(talentId, journeyId),
    queryFn: () =>
      orNullOn404(() => careService.getWellbeingSummary(talentId, requireJourney(journeyId))),
    enabled: isSignedIn && !!token && !!talentId && !!journeyId,
    staleTime: 1000 * 60 * 5,
    retry: retryCareQuery,
  });
}

export function useCareMomentum(talentId: string, journeyId: J) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const careService = createCareService(client);

  return useQuery({
    queryKey: careKeys.momentum(talentId, journeyId),
    queryFn: () => careService.getMomentum(talentId, requireJourney(journeyId)),
    enabled: isSignedIn && !!token && !!talentId && !!journeyId,
    staleTime: 1000 * 60 * 5,
    retry: retryCareQuery,
  });
}

/** Talent-visible scheduled interactions (calls/meetings) on the journey. */
export function useCareInteractions(talentId: string, journeyId: J) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const careService = createCareService(client);

  return useQuery({
    queryKey: careKeys.interactions(talentId, journeyId),
    queryFn: () => careService.getInteractions(talentId, requireJourney(journeyId)),
    enabled: isSignedIn && !!token && !!talentId && !!journeyId,
    staleTime: 1000 * 60 * 5,
    retry: retryCareQuery,
  });
}

export function useCareSupportRequests(talentId: string, journeyId: J) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const careService = createCareService(client);

  return useQuery({
    queryKey: careKeys.support(talentId, journeyId),
    queryFn: () => careService.getSupportRequests(talentId, requireJourney(journeyId)),
    enabled: isSignedIn && !!token && !!talentId && !!journeyId,
  });
}

export function useCreateSupportRequest(talentId: string, journeyId: J) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const careService = createCareService(client);

  return useMutation({
    mutationFn: (input: CreateSupportRequestInput) =>
      careService.createSupportRequest(talentId, input, requireJourney(journeyId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: careKeys.support(talentId, journeyId) });
    },
  });
}

export function useUpdateSupportRequest(talentId: string, journeyId: J) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const careService = createCareService(client);

  return useMutation({
    mutationFn: ({ requestId, input }: { requestId: string; input: UpdateSupportRequestInput }) =>
      careService.updateSupportRequest(talentId, requestId, input, requireJourney(journeyId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: careKeys.support(talentId, journeyId) });
    },
  });
}

export function useScheduleCheckpoints(talentId: string, journeyId: J) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const careService = createCareService(client);

  return useMutation({
    mutationFn: (input: { arrivalDate: string }) =>
      careService.scheduleCheckpoints(talentId, input, requireJourney(journeyId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: careKeys.checkpoints(talentId, journeyId) });
    },
  });
}

// ─── Objectives-based feedback (BL-125) ───

/** The journey's tracked objectives + feedback cycles + entries (X7). */
export function useCareObjectives(talentId: string, journeyId: J) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const careService = createCareService(client);

  return useQuery({
    queryKey: careKeys.objectives(talentId, journeyId),
    queryFn: () => careService.getObjectives(talentId, requireJourney(journeyId)),
    enabled: isSignedIn && !!token && !!talentId && !!journeyId,
    retry: retryCareQuery,
  });
}

/** Talent self-evaluates the journey's objectives for its open cycle. */
export function useSubmitObjectiveSelfEval(talentId: string, journeyId: J) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const careService = createCareService(client);

  return useMutation({
    mutationFn: (input: SubmitObjectiveFeedbackInput) =>
      careService.submitObjectiveSelfEval(talentId, input, requireJourney(journeyId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: careKeys.objectives(talentId, journeyId) });
      // A self-eval that completes a cycle writes a Passport timeline event the
      // momentum stream reads, and emits IRR.
      queryClient.invalidateQueries({ queryKey: careKeys.momentum(talentId, journeyId) });
    },
  });
}
